/**
 * KARDO STUDIOS: custom scripts on top of Prestige 11.4.0
 *
 * Phone product-card images (same behaviour as the Kardo Studios 1.1.1 theme): on screens up to 700px, the card shows
 * its first image and switches to the second image while the finger is on the card. The images are output by
 * snippets/product-card.liquid inside .kardo-mobile-images and styled in assets/kardo.css (section 5).
 *
 * Everything else that the 1.1.1 theme did in custom.js (cart drawer rendering, quick add, cart count) is handled
 * natively by Prestige 11.4.0.
 */
(() => {
  const setTouched = (event, touched) => {
    const images = event.target.closest?.('.product-card__media')?.querySelector('.kardo-mobile-images');

    if (images) {
      images.classList.toggle('is-touched', touched);
    }
  };

  document.addEventListener('touchstart', (event) => setTouched(event, true), { passive: true });
  document.addEventListener('touchend', (event) => setTouched(event, false), { passive: true });
  document.addEventListener('touchcancel', (event) => setTouched(event, false), { passive: true });
})();

/* KARDO: fly-to-cart animation (product page + product card quick add) */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function sizedUrl(src) {
    try {
      const url = new URL(src, location.href);
      url.protocol = location.protocol;
      url.searchParams.set('width', '300');
      return url.toString();
    } catch (e) { return src; }
  }

  function mainProductImage() {
    const og = document.querySelector('meta[property="og:image"]');
    if (og && og.content) return sizedUrl(og.content);
    const img = document.querySelector('main img');
    return img ? sizedUrl(img.currentSrc || img.src) : null;
  }

  function findCart() {
    const links = [...document.querySelectorAll('a[href*="/cart"]')].filter(a => {
      const r = a.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.top < window.innerHeight / 2;
    });
    return links[0] || null;
  }

  function isAddButton(btn) {
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return false;
    const form = btn.form || btn.closest('form');
    return !!(form && btn.type === 'submit' && (form.getAttribute('action') || '').includes('/cart/add'));
  }

  function fly(fromRect, src) {
    const cart = findCart();
    const t = cart ? cart.getBoundingClientRect()
                   : { left: window.innerWidth - 80, top: 20, width: 40, height: 20 };
    const size = 110;

    const startX = fromRect.left + fromRect.width / 2 - size / 2;
    const startY = fromRect.top + fromRect.height / 2 - size / 2;
    const dx = (t.left + t.width / 2 - size / 2) - startX;
    const dy = (t.top + t.height / 2 - size / 2) - startY;

    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.className = 'kardo-fly-img';
    img.style.left = startX + 'px';
    img.style.top = startY + 'px';
    img.style.width = size + 'px';
    img.style.height = size + 'px';
    document.body.appendChild(img);

    const anim = img.animate([
      { transform: 'translate(0, 0) scale(0.5) rotate(0deg)', opacity: 0 },
      { transform: `translate(${dx * 0.3}px, ${dy * 0.3 - 120}px) scale(1) rotate(-8deg)`, opacity: 1, offset: 0.35 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.15) rotate(12deg)`, opacity: 0.7 }
    ], { duration: 900, easing: 'cubic-bezier(.5, 0, .3, 1)' });

    anim.onfinish = () => {
      img.remove();
      if (cart) {
        cart.animate(
          [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }],
          { duration: 350, easing: 'ease-out' }
        );
      }
    };
  }

  document.addEventListener('click', (e) => {
    const card = e.target.closest('.product-card');

    // Product cards: the + button or a quick size button
    if (card) {
      const btn = e.target.closest('.kardo-quick-sizes__button, .product-card__quick-add-button');
      if (!btn) return;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      if (btn.classList.contains('product-card__quick-add-button') && !isAddButton(btn)) return;

      const img = card.querySelector('.product-card__media img') || card.querySelector('img');
      if (!img) return;
      fly(img.getBoundingClientRect(), sizedUrl(img.currentSrc || img.src));
      return;
    }

    // Main product page ADD TO CART
    const btn = e.target.closest('button[type="submit"]');
    if (!btn || !isAddButton(btn)) return;
    const src = mainProductImage();
    if (src) fly(btn.getBoundingClientRect(), src);
  });
})();
/* KARDO: glass drawers open/close animation
   Used by the mobile menu (snippets/header-sidebar.liquid), the cart drawer (sections/cart-drawer.liquid) and the
   filters drawer (sections/main-collection.liquid, main-search.liquid), styled in assets/kardo.css sections 4, 4B, 13. The floating card fades and scales in from its top corner instead of sliding in
   from the edge of the screen; in the menu, the "Los más deseados" pills fade in after the links. */
(() => {
  const ease = 'cubic-bezier(.22, 1, .36, 1)';
  const speed = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1);
  const done = (animations) => ({ finished: Promise.all(animations.map((a) => a.finished.catch(() => {}))) });

  const stopPrevious = (drawer) => {
    (drawer._kardoAnimations || []).forEach((a) => a.cancel());
    drawer._kardoAnimations = [];
  };

  const patch = (tagName, glassClass, fromRight) => {
    customElements.whenDefined(tagName).then(() => {
      const proto = customElements.get(tagName).prototype;
      const originalEnter = proto.createEnterAnimationControls;
      const originalLeave = proto.createLeaveAnimationControls;
      const x = fromRight ? 1 : -1;

      proto.createEnterAnimationControls = function () {
        if (!this.classList.contains(glassClass)) return originalEnter.call(this);

        stopPrevious(this);
        const s = speed();
        const overlay = this.getShadowPartByName('overlay');
        const content = this.getShadowPartByName('content');
        const featured = this.querySelector('.kardo-menu-featured');

        const animations = [
          overlay.animate({ opacity: [0, 1] }, { duration: 300 * s, easing: 'ease-out', fill: 'both' }),
          content.animate(
            { opacity: [0, 1], transform: [`translate(${14 * x}px, -10px) scale(0.94)`, 'translate(0, 0) scale(1)'] },
            { duration: 450 * s, easing: ease, fill: 'both' }
          )
        ];

        if (featured) {
          this._kardoAnimations.push(
            featured.animate(
              { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] },
              { duration: 400 * s, delay: 650 * s, easing: ease, fill: 'both' }
            )
          );
        }

        this._kardoAnimations.push(...animations);
        return done(animations);
      };

      proto.createLeaveAnimationControls = function () {
        if (!this.classList.contains(glassClass)) return originalLeave.call(this);

        stopPrevious(this);
        const s = speed();
        const overlay = this.getShadowPartByName('overlay');
        const content = this.getShadowPartByName('content');

        const animations = [
          overlay.animate({ opacity: [1, 0] }, { duration: 250 * s, easing: 'ease-in', fill: 'both' }),
          content.animate(
            { opacity: [1, 0], transform: ['translate(0, 0) scale(1)', `translate(${10 * x}px, -6px) scale(0.96)`] },
            { duration: 250 * s, easing: 'ease-in', fill: 'both' }
          )
        ];

        this._kardoAnimations.push(...animations);
        return done(animations);
      };
    });
  };

  patch('header-sidebar', 'kardo-glass-menu', false);
  patch('cart-drawer', 'kardo-glass-cart', true);
  patch('facets-drawer', 'kardo-glass-facets', true);
})();


/* KARDO: fuse free shipping bar with the bomb discount (snippets/free-shipping-bar.liquid, assets/kardo.css 14.3)
   Three steps, based on the cart products (before order discounts, so the 10% itself never moves the bar back):
   1. Below the free shipping amount: the spark burns along the fuse towards the bomb.
   2. Free shipping reached: "¡Envío gratis alcanzado!" and the bomb is about to explode ("armed"): the spark sits on it.
   3. One more product (the cart still reaches free shipping without its cheapest product): the bomb explodes and the
      discount code from Theme settings > Cart is applied to the cart. If the cart goes back to step 1 or 2, the code is
      removed again. The cart drawer re-renders the bar on every change, so the last position is remembered to animate
      from it. */
(() => {
  const key = 'kardo-free-shipping-progress';
  const stageKey = 'kardo-bomb-stage';
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, String(v)); } catch (e) {} }
  };

  const escapeHtml = (text) => String(text || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  let cartRequest = null;
  const getCart = () => {
    if (!cartRequest) {
      cartRequest = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .finally(() => setTimeout(() => { cartRequest = null; }, 300));
    }
    return cartRequest;
  };

  const stageOf = (bar, cart) => {
    const threshold = parseFloat(bar.dataset.kardoThresholdCents) || 0;
    const items = cart.items.filter((item) => item.requires_shipping);
    const total = items.reduce((sum, item) => sum + item.final_line_price, 0);
    const count = items.reduce((sum, item) => sum + item.quantity, 0);
    const cheapest = items.length ? Math.min(...items.map((item) => item.final_price)) : 0;
    const pct = threshold > 0 ? Math.min(100, (total / threshold) * 100) : 100;

    let stage = 1;
    if (threshold <= 0 || total >= threshold) {
      stage = count >= 2 && total - cheapest >= threshold ? 3 : 2;
    }
    return { stage, pct, total, threshold };
  };

  const setMessage = (bar, stage) => {
    const message = bar.querySelector(':scope > span');
    if (!message || !bar.dataset.kardoBombCode) return;

    if (stage === 2) {
      message.innerHTML = `<span class="kardo-bomb-msg"><span>${escapeHtml(bar.dataset.kardoBombArmedTitle)}</span> <strong>${escapeHtml(bar.dataset.kardoBombArmedText)}</strong></span>`;
    } else if (stage === 3) {
      message.innerHTML = `<span class="kardo-bomb-msg"><strong>${escapeHtml(bar.dataset.kardoBombDoneText)}</strong></span>`;
    }
  };

  let syncing = false;
  const syncCode = async (bar, cart, stage) => {
    const code = (bar.dataset.kardoBombCode || '').trim();
    if (!code || syncing) return;

    const applied = (cart.discount_codes || []).map((d) => d.code.toLowerCase());
    const has = applied.includes(code.toLowerCase());
    const want = stage === 3;
    if (has === want) return;

    const others = (cart.discount_codes || []).filter((d) => d.applicable && d.code.toLowerCase() !== code.toLowerCase()).map((d) => d.code);
    const codes = want ? [...others, code] : others;

    syncing = true;
    try {
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') })
      });
      // Let the explosion play before the cart re-renders with the new totals
      setTimeout(() => {
        syncing = false;
        if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart') {
          window.location.reload();
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      }, want && !reduce() ? 1500 : 0);
    } catch (e) {
      syncing = false;
    }
  };

  // The bar arrives already drawn in its final state (snippets/free-shipping-bar.liquid). When it is new (the cart
  // drawer re-renders it on every change), it is moved back to the last position without animation, then animated to
  // the new one. Everything here is synchronous so nothing is painted in between.
  const render = (bar) => {
    const track = bar.querySelector('.free-shipping-bar__track');
    const fill = bar.querySelector('.free-shipping-bar__fill');
    if (!track || !fill) return;

    const pct = parseFloat(bar.dataset.kardoPct);
    const stage = parseInt(bar.dataset.kardoStage, 10) || 1;
    if (isNaN(pct)) return;

    const lastPct = parseFloat(store.get(key));
    const lastStage = parseInt(store.get(stageKey), 10) || 0;

    const setAll = (v) => {
      track.style.setProperty('--kardo-progress', v);
      fill.style.width = v + '%';
    };

    if (!isNaN(lastPct) && lastPct !== pct && !reduce()) {
      track.classList.add('kardo-no-transition');
      setAll(lastPct);
      track.classList.toggle('is-armed', lastStage === 2);
      track.classList.toggle('is-complete', lastStage === 3);
      track.offsetWidth; // apply the start position before animating
      track.classList.remove('kardo-no-transition');
      requestAnimationFrame(() => {
        setAll(pct);
        track.classList.toggle('is-armed', stage === 2);
        track.classList.toggle('is-complete', stage === 3);
        track.classList.add('is-running');
        clearTimeout(track._kardoRunTimer);
        track._kardoRunTimer = setTimeout(() => track.classList.remove('is-running'), 900);
      });
    } else {
      setAll(pct);
      track.classList.toggle('is-armed', stage === 2);
      track.classList.toggle('is-complete', stage === 3);
    }

    if (stage === 3 && lastStage && lastStage < 3 && !reduce()) {
      track.classList.add('is-celebrating');
      setTimeout(() => track.classList.remove('is-celebrating'), 2200);
    }

    setMessage(bar, stage);
    store.set(key, pct);
    store.set(stageKey, stage);
  };

  // Apply or remove the discount code, from fresh cart data
  const checkCode = async (bar) => {
    if (!bar.dataset.kardoBombCode) return;
    let cart;
    try { cart = await getCart(); } catch (e) { return; }
    if (!bar.isConnected) return;
    syncCode(bar, cart, stageOf(bar, cart).stage);
  };

  const watch = (bar) => {
    if (bar._kardoFuse) return;
    bar._kardoFuse = true;
    render(bar);
    checkCode(bar);
    // The theme rewrites the message of the old bar when the cart changes, just before the drawer replaces it: put ours
    // back right away so its default text never flashes
    new MutationObserver(() => setMessage(bar, parseInt(bar.dataset.kardoStage, 10) || 1)).observe(bar, { attributes: true, attributeFilter: ['total-price'] });
  };

  const scan = () => document.querySelectorAll('free-shipping-bar').forEach(watch);

  customElements.whenDefined('free-shipping-bar').then(scan);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  document.addEventListener('cart:refresh', () => { cartRequest = null; });
  document.addEventListener('cart:change', () => { cartRequest = null; });
  new MutationObserver((records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType === 1 && (n.matches('free-shipping-bar') || n.querySelector('free-shipping-bar'))) { scan(); return; }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* KARDO: shop category bar on desktop (sections/header.liquid, assets/kardo.css 13.16)
   The "Shop" menu item opens a glass bar of category pills under the header; it closes when clicking outside it,
   pressing Escape, or clicking "Shop" again. */
(() => {
  const setOpen = (toggle, bar, open) => {
    toggle.setAttribute('aria-expanded', String(open));
    clearTimeout(bar._kardoCloseTimer);
    if (open) {
      bar.classList.remove('is-closing');
      bar.hidden = false;
      bar.classList.add('is-open');
    } else if (!bar.hidden) {
      // Slide back up under the header, then hide
      bar.classList.remove('is-open');
      bar.classList.add('is-closing');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      bar._kardoCloseTimer = setTimeout(() => {
        bar.hidden = true;
        bar.classList.remove('is-closing');
      }, reduce ? 0 : 260);
    }
  };

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('.kardo-shop-toggle');
    const bar = document.getElementById('kardo-shop-bar');
    if (!bar) return;

    const openToggle = document.querySelector('.kardo-shop-toggle[aria-expanded="true"]');

    if (toggle) {
      e.preventDefault();
      setOpen(toggle, bar, toggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    if (openToggle && !bar.contains(e.target)) setOpen(openToggle, bar, false);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const openToggle = document.querySelector('.kardo-shop-toggle[aria-expanded="true"]');
    const bar = document.getElementById('kardo-shop-bar');
    if (openToggle && bar) {
      setOpen(openToggle, bar, false);
      openToggle.focus();
    }
  });

  window.addEventListener('resize', () => {
    const openToggle = document.querySelector('.kardo-shop-toggle[aria-expanded="true"]');
    const bar = document.getElementById('kardo-shop-bar');
    if (openToggle && bar && openToggle.offsetParent === null) setOpen(openToggle, bar, false);
  });
})();

/* =====================================================================================================================
   KARDO: "Completa el look" slider in the cart drawer
   Loads sections/kardo-cart-look.liquid through Shopify's recommendations endpoint for the newest cart item.
   Tries the Search & Discovery complementary products first, then the automatic "related" ones.
   Results are cached per product so the slider does not flicker when the drawer re-renders.
   ===================================================================================================================== */
(() => {
  if (customElements.get('kardo-cart-look')) return;

  const cache = new Map();
  let lastHtml = '';

  async function loadCards(url, productId) {
    if (cache.has(productId)) return cache.get(productId);

    const request = (async () => {
      for (const intent of ['complementary', 'related']) {
        try {
          const response = await fetch(`${url}?product_id=${productId}&limit=10&intent=${intent}&section_id=kardo-cart-look`);
          if (!response.ok) continue;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const results = doc.querySelector('.kardo-look-results');
          if (results && results.querySelector('.kardo-look__card')) return results.innerHTML;
        } catch (error) {
          /* try the next intent */
        }
      }
      return '';
    })();

    cache.set(productId, request);
    return request;
  }

  class KardoCartLook extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('.kardo-look__track');

      this.addEventListener('click', (event) => this.onClick(event));
      this._outside = (event) => {
        if (!event.target.closest('.kardo-look__card.is-picking')) this.closeSizes();
      };
      document.addEventListener('click', this._outside, true);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._outside, true);
    }

    async render() {
      const productId = this.dataset.productId;
      if (!productId || !this.track) return;

      /* Show the previous cards right away so the drawer does not jump while the new ones load */
      if (lastHtml) this.paint(lastHtml);

      const html = await loadCards(this.dataset.url || '/recommendations/products', productId);
      if (!this.isConnected) return;

      if (html) lastHtml = html;
      this.paint(html || lastHtml);
    }

    paint(html) {
      const scroll = this.track.scrollLeft;
      this.track.innerHTML = html;

      const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
      this.track.querySelectorAll('.kardo-look__card').forEach((card) => {
        if (exclude.includes(card.dataset.productId)) card.remove();
      });

      const count = this.track.querySelectorAll('.kardo-look__card').length;
      this.hidden = count === 0;
      this.classList.toggle('is-single', count < 3);
      this.track.scrollLeft = scroll;
    }

    closeSizes(except) {
      this.querySelectorAll('.kardo-look__card.is-picking').forEach((card) => {
        if (card === except) return;
        card.classList.remove('is-picking');
        card.querySelector('[data-kardo-look-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    }

    onClick(event) {
      const arrow = event.target.closest('[data-kardo-look-prev], [data-kardo-look-next]');
      if (arrow) {
        const card = this.track.querySelector('.kardo-look__card');
        const step = card ? (card.offsetWidth + 10) * 2 : 240;
        this.track.scrollBy({ left: arrow.hasAttribute('data-kardo-look-prev') ? -step : step, behavior: 'smooth' });
        return;
      }

      const toggle = event.target.closest('[data-kardo-look-toggle]');
      if (toggle) {
        event.preventDefault();
        const card = toggle.closest('.kardo-look__card');
        const open = !card.classList.contains('is-picking');
        this.closeSizes(card);
        card.classList.toggle('is-picking', open);
        toggle.setAttribute('aria-expanded', String(open));
        return;
      }

      const add = event.target.closest('[data-kardo-look-add]');
      if (add && !add.disabled) {
        event.preventDefault();
        this.addToCart(add);
      }
    }

    async addToCart(button) {
      const card = button.closest('.kardo-look__card');
      if (card.classList.contains('is-loading')) return;
      card.classList.add('is-loading');

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: Number(button.dataset.kardoLookAdd), quantity: 1 }] })
        });

        if (!response.ok) throw new Error('add failed');

        card.classList.remove('is-loading', 'is-picking');
        card.classList.add('is-added');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (error) {
        card.classList.remove('is-loading');
        card.classList.add('is-error');
        setTimeout(() => card.classList.remove('is-error'), 600);
      }
    }
  }

  customElements.define('kardo-cart-look', KardoCartLook);
})();


/* KARDO: "Avísame cuando salga" for "Pronto" categories (snippets/kardo-shop-categories.liquid, assets/kardo.css 14.5)
   A "Pronto" pill opens the e-mail form under the category list and sets the tag of that category
   ("pronto-hoodies", ...). After sending, the page reloads and a small glass message confirms it. */
(() => {
  document.addEventListener('click', (event) => {
    const pill = event.target.closest('[data-kardo-soon-tag]');
    if (!pill) return;

    const list = pill.closest('ul');
    let form = list ? list.nextElementSibling : null;
    while (form && !form.classList.contains('kardo-soon-form')) form = form.nextElementSibling;
    const panel = form ? form.querySelector('.kardo-soon-form__panel') : null;
    if (!panel) return;

    const isOpen = pill.getAttribute('aria-expanded') === 'true';
    list.querySelectorAll('[data-kardo-soon-tag]').forEach((p) => p.setAttribute('aria-expanded', 'false'));

    if (isOpen) {
      panel.hidden = true;
      return;
    }

    pill.setAttribute('aria-expanded', 'true');
    panel.querySelector('[data-kardo-soon-name]').textContent = pill.dataset.kardoSoonLabel;
    panel.querySelector('[data-kardo-soon-tags]').value = 'pronto, ' + pill.dataset.kardoSoonTag;
    panel.hidden = false;

    const input = panel.querySelector('input[type="email"]');
    if (input && window.matchMedia('(pointer: fine)').matches) input.focus({ preventScroll: true });
  });

  const toast = (text) => {
    const el = document.createElement('div');
    el.className = 'kardo-toast';
    el.setAttribute('role', 'status');
    el.textContent = text;
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => el.remove(), 400);
    }, 4500);
  };

  const check = () => {
    if (document.querySelector('[data-kardo-soon-done]')) {
      toast('Listo, te avisaremos cuando salga.');
    } else if (document.querySelector('.kardo-soon-form__error')) {
      toast('Revisa tu correo e inténtalo de nuevo.');
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', check); else check();
})();
